import { useEffect, useRef, useState } from "react";
import { AssetTypes } from "@zapier/utils";
import type {
  PrivateFolderOwnerMismatch,
  PrivateFolderOwnerPolicy,
} from "@zapier/client";
import { MoveModalInner } from "./core/MoveModalInner";
import {
  useEmitFolderActionEvent,
  FolderActionInteractionOption,
} from "../../../events/";
import { MoveModalProps, OnMoveOptions } from "./core/types";
import { TransferOwnershipPrompt } from "./TransferOwnershipPrompt";
import { useMoveAssetActions } from "./useMoveAssetActions";

interface FolderSelection {
  selectedFolderId: string;
  selectedFolderName: string;
}

interface PendingOwnerTransfer extends FolderSelection {
  assetIds: string[];
  mismatch: PrivateFolderOwnerMismatch;
}

export function MoveAssetModal({
  assetType,
  assetIds,
  assetsAreShared = false,
  assetParentId,
  assetTitle,
  confirmsOwnerTransfer = false,
  isOpen,
  isAssetOwnedByUser = false,
  onSettled,
  setIsOpen,
}: MoveModalProps) {
  const { moveAssetsToFolderWithToasts, isPending } = useMoveAssetActions({
    assetTitle,
    assetParentId,
    onSettled,
  });
  const { emit } = useEmitFolderActionEvent();
  const [pendingOwnerTransfer, setPendingOwnerTransfer] = useState<
    PendingOwnerTransfer | undefined
  >();
  // Bumped whenever the dialog closes, so a reply to a request started in an
  // earlier session cannot reopen the prompt or move a different selection.
  const sessionReference = useRef(0);

  useEffect(() => {
    if (isOpen) return;
    sessionReference.current += 1;
    setPendingOwnerTransfer(undefined);
  }, [isOpen]);

  // Only Zaps can land in a private folder owned by someone else.
  const asksBeforeTransfer =
    confirmsOwnerTransfer && (assetType ?? AssetTypes.Zap) === AssetTypes.Zap;

  const moveWithPolicy = (
    { selectedFolderId, selectedFolderName }: FolderSelection,
    movedAssetIds: string[],
    privateFolderOwnerPolicy: PrivateFolderOwnerPolicy,
  ) => {
    const session = sessionReference.current;
    const isCurrent = () => sessionReference.current === session;

    moveAssetsToFolderWithToasts({
      assetIds: movedAssetIds,
      selectedFolderId,
      selectedFolderName,
      privateFolderOwnerPolicy,
      onPrivateFolderOwnerMismatch:
        privateFolderOwnerPolicy === "refuse"
          ? (mismatch) => {
              if (!isCurrent()) return;
              setPendingOwnerTransfer({
                assetIds: movedAssetIds,
                mismatch,
                selectedFolderId,
                selectedFolderName,
              });
            }
          : undefined,
      onSettled: () => {
        if (!isCurrent()) return;
        setPendingOwnerTransfer(undefined);
        setIsOpen(false);
      },
    });
  };

  const handleMoveAssetsToFolder = ({
    selectedFolderId,
    selectedFolderName,
  }: OnMoveOptions) => {
    if (!selectedFolderId) {
      return;
    }

    if (!assetIds || assetIds?.length === 0) {
      return;
    }
    emit({
      optionSelected: FolderActionInteractionOption.MoveFolder,
    });

    if (asksBeforeTransfer) {
      moveWithPolicy(
        { selectedFolderId, selectedFolderName },
        assetIds,
        "refuse",
      );
      return;
    }

    moveAssetsToFolderWithToasts({
      assetIds,
      selectedFolderId,
      selectedFolderName,
    });
    setIsOpen(false);
  };

  // Do not display the modal if isOpen==false
  if (!isOpen) {
    return;
  }

  if (pendingOwnerTransfer) {
    return (
      <TransferOwnershipPrompt
        folderName={pendingOwnerTransfer.selectedFolderName}
        folderOwnerId={pendingOwnerTransfer.mismatch.folderOwnerId}
        assetCount={pendingOwnerTransfer.mismatch.assets.length}
        isPending={isPending}
        onCancel={() => setPendingOwnerTransfer(undefined)}
        onConfirm={() =>
          moveWithPolicy(
            pendingOwnerTransfer,
            pendingOwnerTransfer.assetIds,
            "transfer",
          )
        }
      />
    );
  }

  return (
    <MoveModalInner
      assetType={assetType || AssetTypes.Zap}
      assetIds={assetIds}
      assetsAreShared={assetsAreShared}
      assetParentId={assetParentId}
      assetTitle={assetTitle}
      isAssetOwnedByUser={isAssetOwnedByUser}
      forceSubmitDisabled={asksBeforeTransfer && isPending}
      onMove={handleMoveAssetsToFolder}
      setIsOpen={setIsOpen}
    />
  );
}
